import { useEffect, useRef, useState, useCallback } from "react";
import { API } from "@/lib/api";
import { parseYouTube, loadYouTubeApi } from "@/lib/media";

const MEDIA_BASE = API.replace(/\/api$/, "");
export const mediaUrl = (u) => (u && u.startsWith("http") ? u : `${MEDIA_BASE}${u}`);
const DEFAULT_IMAGE_SECONDS = 8;
const DEFAULT_EMBED_SECONDS = 30;
const FILL = { position: "absolute", top: 0, left: 0, width: "100%", height: "100%" };

function YouTubePlayer({ yt, soundOn, volume, onEnded, single }) {
  const hostRef = useRef(null);
  const playerRef = useRef(null);
  const readyRef = useRef(false);
  const [failed, setFailed] = useState(false);
  const endRef = useRef(onEnded);
  const soundRef = useRef({ soundOn, volume });
  endRef.current = onEnded;
  soundRef.current = { soundOn, volume };

  const applySound = (p) => {
    const s = soundRef.current;
    try {
      if (s.soundOn && s.volume > 0) {
        p.unMute();
        p.setVolume(Math.round(s.volume * 100));
      } else p.mute();
    } catch (e) { /* player not ready */ }
  };

  useEffect(() => {
    let cancelled = false;
    const host = hostRef.current;
    const el = document.createElement("div");
    host.appendChild(el);
    readyRef.current = false;
    const safety = setTimeout(() => { if (!readyRef.current && !single) endRef.current(); }, 25000);
    loadYouTubeApi().then((YT) => {
      if (cancelled) return;
      playerRef.current = new YT.Player(el, {
        width: "100%",
        height: "100%",
        videoId: yt.id || undefined,
        playerVars: {
          autoplay: 1, mute: 1, playsinline: 1, rel: 0, controls: 0, modestbranding: 1, iv_load_policy: 3,
          ...(yt.list ? { listType: "playlist", list: yt.list } : {}),
        },
        events: {
          onReady: (e) => { readyRef.current = true; applySound(e.target); e.target.playVideo(); },
          onStateChange: (e) => {
            if (e.data !== 0) return;
            const p = e.target;
            if (yt.list) {
              const pl = p.getPlaylist() || [];
              if (p.getPlaylistIndex() < pl.length - 1) return;
            }
            if (single) { p.seekTo(0); p.playVideo(); } else endRef.current();
          },
          onError: () => { if (single) setFailed(true); else setTimeout(() => { if (!cancelled) endRef.current(); }, 2000); },
        },
      });
    });
    return () => {
      cancelled = true;
      clearTimeout(safety);
      try { if (playerRef.current) playerRef.current.destroy(); } catch (e) { /* ignore */ }
      playerRef.current = null;
      host.innerHTML = "";
    };
  }, [yt.id, yt.list, single]);

  useEffect(() => {
    if (readyRef.current && playerRef.current) applySound(playerRef.current);
  }, [soundOn, volume]);

  return (
    <>
      <div ref={hostRef} style={FILL} className="tv-yt-host" data-testid="tv-youtube" />
      {failed && (
        <div style={FILL} className="flex items-center justify-center text-center text-cream/70 p-8 bg-black" data-testid="tv-youtube-error">
          Este vídeo do YouTube não permite exibição fora do YouTube. Escolha outro link nas Configurações.
        </div>
      )}
    </>
  );
}

function VideoItem({ url, soundOn, volume, single, onEnded, onBlocked }) {
  const ref = useRef(null);
  const errTimer = useRef(null);
  useEffect(() => () => clearTimeout(errTimer.current), []);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const wantSound = soundOn && volume > 0;
    v.muted = !wantSound;
    v.volume = Math.max(0, Math.min(1, volume));
    const p = v.play();
    if (p && p.catch) {
      p.catch(() => {
        if (!v.muted) {
          v.muted = true;
          v.play().catch(() => {});
          if (onBlocked) onBlocked();
        }
      });
    }
  }, [url, soundOn, volume]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <video
      ref={ref}
      src={mediaUrl(url)}
      style={{ ...FILL, objectFit: "contain" }}
      autoPlay playsInline preload="auto"
      loop={single}
      onEnded={() => { if (!single) onEnded(); }}
      onError={() => { if (!single) errTimer.current = setTimeout(onEnded, 3000); }}
      data-testid="tv-video"
    />
  );
}

export function MediaPanel({ items, volume = 1, soundOn, onAutoplayBlocked, skipSignal = 0 }) {
  const [idx, setIdx] = useState(0);
  const safeIdx = idx % items.length;
  const item = items[safeIdx];
  const single = items.length < 2;
  const yt = item.type === "embed" ? parseYouTube(item.url) : null;
  const advance = useCallback(() => setIdx((i) => (i + 1) % items.length), [items.length]);
  const limit = item.type !== "video" && !yt && Number(item.duration) > 0 ? Number(item.duration) : 0;

  useEffect(() => {
    if (skipSignal > 0 && items.length > 1) advance();
  }, [skipSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (single) return;
    if (item.type === "video" || yt) return;
    const secs = limit || (item.type === "embed" ? DEFAULT_EMBED_SECONDS : DEFAULT_IMAGE_SECONDS);
    const t = setTimeout(advance, secs * 1000);
    return () => clearTimeout(t);
  }, [safeIdx, items.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const key = item.url + safeIdx;
  return (
    <div style={{ ...FILL, background: "#000", overflow: "hidden" }} data-testid="tv-media-panel">
      {item.type === "video" ? (
        <VideoItem key={key} url={item.url} soundOn={soundOn} volume={volume} single={single}
          onEnded={advance} onBlocked={onAutoplayBlocked} />
      ) : yt ? (
        <YouTubePlayer key={key} yt={yt} soundOn={soundOn} volume={volume} single={single} onEnded={advance} />
      ) : item.type === "embed" ? (
        <iframe key={key} title="tv-embed" src={item.url} style={{ ...FILL, border: 0 }}
          allow="autoplay; encrypted-media; fullscreen" allowFullScreen />
      ) : (
        <div key={key} style={FILL} className="animate-fade-up">
          <img src={mediaUrl(item.url)} alt="" aria-hidden="true"
            style={{ ...FILL, objectFit: "cover", filter: "blur(28px) brightness(0.55)", transform: "scale(1.15)" }} />
          <img src={mediaUrl(item.url)} alt="" style={{ ...FILL, objectFit: "contain" }} data-testid="tv-image" />
        </div>
      )}
    </div>
  );
}
